import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { SolutionInstanceDto } from 'app/@types/dto/solution-instance-dto.model';
import { Subject, takeUntil } from 'rxjs';
import { SolutionHttpService } from '@services/datasources/solution-http-service';
import { ResultInstanceDto } from 'app/@types/dto/result-instance-dto.model';
import { CustomTranslateService } from '@services/common/custom-translate.service';

export interface SolutionStateModel {
    questionnaireId: string | undefined;
    organizationId: string | undefined;
    solutionInstancesPerSolutionCode: Map<string, SolutionInstanceDto>;
    relevantResultsPerSolutionCode: Map<string, ResultInstanceDto[]>;
    visitedSolutionCode: string | undefined;
    isStale: boolean;
    isLoading: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-namespace -- NGXS action grouping requires namespace for combined value+type semantics
export namespace SolutionAction {
    export class Activate {
        static readonly type = '[Solutions UI] Activate Solutions';

        constructor(
            public orgId: string,
            public questionnaireId: string,
        ) {}
    }

    export class CreateAction {
        static readonly type = '[Solution UI] Create SolutionInstance';

        constructor(
            public instance: SolutionInstanceDto,
            public callCompletion: Subject<SolutionInstanceDto | undefined>,
        ) {}
    }

    export class UpdateAction {
        static readonly type = '[Solution UI] CreateUpdate SolutionInstance';

        constructor(
            public instance: SolutionInstanceDto,
            public callCompletion: Subject<SolutionInstanceDto | undefined>,
        ) {}
    }

    export class DeleteAction {
        static readonly type = '[Solution UI] Delete QuestionInstance';

        constructor(
            public instance: SolutionInstanceDto,
            public callCompletion: Subject<SolutionInstanceDto | undefined>,
        ) {}
    }

    export class SetVisitedSolutionCode {
        static readonly type = '[Questionnaire UI] setVisitedSolution';

        constructor(public solutionCode: string) {}
    }

    export class Invalidate {
        static readonly type = '[Solution UI] Invalidate SolutionState';
    }

    export class Deactivate {
        static readonly type = '[Solution UI] Deactivate Solutions';
    }

    export class RecomputeAllSolutions {
        static readonly type = '[Solution UI] Recompute All Solutions';

        constructor(
            public orgId: string,
            public questionnaireId: string,
        ) {}
    }
}

@State<SolutionStateModel>({
    name: 'solution',
    defaults: {
        questionnaireId: undefined,
        organizationId: undefined,
        solutionInstancesPerSolutionCode: new Map(),
        relevantResultsPerSolutionCode: new Map(),
        visitedSolutionCode: undefined,
        isStale: false,
        isLoading: false,
    },
})
@Injectable()
export class SolutionState {
    private readonly activateTeardown$ = new Subject<void>();
    private readonly recomputeTeardown$ = new Subject<void>();

    constructor(
        private solutionService: SolutionHttpService,
        private translateService: CustomTranslateService,
    ) {}

    @Action(SolutionAction.Activate)
    activate(ctx: StateContext<SolutionStateModel>, action: SolutionAction.Activate) {
        this.activateTeardown$.next();
        this.recomputeTeardown$.next();
        ctx.patchState({
            solutionInstancesPerSolutionCode: new Map(),
            relevantResultsPerSolutionCode: new Map(),
            questionnaireId: action.questionnaireId,
            organizationId: action.orgId,
            isStale: false,
            isLoading: true,
        });
        const untilActivate = this.activateTeardown$;
        this.solutionService
            .getAndupdateAllSolutionInstanceIfNeeded(
                action.orgId,
                action.questionnaireId,
                this.translateService.currentLang,
            )
            .pipe(takeUntil(untilActivate))
            .subscribe({
                next: (results) => {
                    const relevantResultsPerSolutionCode = new Map<string, ResultInstanceDto[]>();
                    const solutionInstancesPerSolutionCode = new Map<string, SolutionInstanceDto>();

                    Object.entries(results).forEach(([key, value]) => {
                        relevantResultsPerSolutionCode.set(key, value.resultInstances);
                        if (value.solutionInstance) {
                            solutionInstancesPerSolutionCode.set(key, value.solutionInstance);
                        }
                    });

                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutionInstancesPerSolutionCode,
                        relevantResultsPerSolutionCode: relevantResultsPerSolutionCode,
                        questionnaireId: action.questionnaireId,
                        organizationId: action.orgId,
                        isStale: false,
                        isLoading: false,
                    });
                },
                error: () => {
                    ctx.patchState({
                        solutionInstancesPerSolutionCode: new Map(),
                        isLoading: false,
                    });
                },
            });
    }

    @Action(SolutionAction.Deactivate)
    deactivate(ctx: StateContext<SolutionStateModel>, _action: SolutionAction.Deactivate) {
        this.activateTeardown$.next();
        this.recomputeTeardown$.next();
        ctx.patchState({
            questionnaireId: undefined,
            organizationId: undefined,
            solutionInstancesPerSolutionCode: new Map(),
            relevantResultsPerSolutionCode: new Map(),
            visitedSolutionCode: undefined,
            isStale: false,
            isLoading: false,
        });
    }

    @Action(SolutionAction.CreateAction)
    create(ctx: StateContext<SolutionStateModel>, action: SolutionAction.CreateAction) {
        const orgId = ctx.getState().organizationId;
        if (orgId) {
            this.solutionService.createSolutionInstance(orgId, action.instance).subscribe({
                next: (si) => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode]);

                    if (si) {
                        solutions.set(action.instance.solutionTemplateCode, si);
                    }

                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                    action.callCompletion.next(si);
                },
                error: () => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode]);
                    action.callCompletion.next(undefined);
                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                },
            });
        }
    }

    @Action(SolutionAction.UpdateAction)
    update(ctx: StateContext<SolutionStateModel>, action: SolutionAction.UpdateAction) {
        const orgId = ctx.getState().organizationId;
        if (orgId) {
            this.solutionService.updateSolutionInstance(orgId, action.instance).subscribe({
                next: (si) => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode]);

                    if (si) {
                        solutions.set(action.instance.solutionTemplateCode, si);
                    }

                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                    action.callCompletion.next(si);
                },
                error: () => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode]);
                    action.callCompletion.next(undefined);
                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                },
            });
        }
    }

    @Action(SolutionAction.DeleteAction)
    delete(ctx: StateContext<SolutionStateModel>, action: SolutionAction.DeleteAction) {
        const orgId = ctx.getState().organizationId;
        if (orgId) {
            this.solutionService.delete(orgId, action.instance).subscribe({
                next: (bool) => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode]);
                    if (bool) {
                        if (action.instance.uid) {
                            solutions.delete(action.instance.solutionTemplateCode);
                        }
                        action.callCompletion.next(action.instance);
                        ctx.patchState({
                            solutionInstancesPerSolutionCode: solutions,
                        });
                    }
                },
                error: () => {
                    const solutions = new Map([...ctx.getState().solutionInstancesPerSolutionCode]);
                    action.callCompletion.next(undefined);
                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutions,
                    });
                },
            });
        }
    }

    @Action(SolutionAction.SetVisitedSolutionCode)
    setVisitedQuestionCode(ctx: StateContext<SolutionStateModel>, action: SolutionAction.SetVisitedSolutionCode) {
        ctx.patchState({
            visitedSolutionCode: action.solutionCode,
        });
    }

    @Action(SolutionAction.Invalidate)
    invalidate(ctx: StateContext<SolutionStateModel>) {
        if (!ctx.getState().isLoading) {
            ctx.patchState({ isStale: true });
        }
    }

    @Action(SolutionAction.RecomputeAllSolutions)
    recomputeAllSolutions(ctx: StateContext<SolutionStateModel>, action: SolutionAction.RecomputeAllSolutions) {
        this.recomputeTeardown$.next();
        ctx.patchState({ isStale: false });
        const untilRecompute = this.recomputeTeardown$;
        this.solutionService
            .getAndupdateAllSolutionInstanceIfNeeded(
                action.orgId,
                action.questionnaireId,
                this.translateService.currentLang,
            )
            .pipe(takeUntil(untilRecompute))
            .subscribe({
                next: (results) => {
                    const relevantResultsPerSolutionCode = new Map<string, ResultInstanceDto[]>();
                    const solutionInstancesPerSolutionCode = new Map<string, SolutionInstanceDto>();

                    Object.entries(results).forEach(([key, value]) => {
                        relevantResultsPerSolutionCode.set(key, value.resultInstances);
                        if (value.solutionInstance) {
                            solutionInstancesPerSolutionCode.set(key, value.solutionInstance);
                        }
                    });

                    ctx.patchState({
                        solutionInstancesPerSolutionCode: solutionInstancesPerSolutionCode,
                        relevantResultsPerSolutionCode: relevantResultsPerSolutionCode,
                        isStale: false,
                    });
                },
                error: () => {
                    ctx.patchState({
                        solutionInstancesPerSolutionCode: new Map(),
                    });
                },
            });
    }

    @Selector([SolutionState])
    static getVisitedSolutionCode(state: SolutionStateModel) {
        return state.visitedSolutionCode;
    }

    @Selector([SolutionState])
    static getSolutions(state: SolutionStateModel) {
        return state.solutionInstancesPerSolutionCode;
    }

    @Selector([SolutionState])
    static getRelevantResultsPerSolutionCode(state: SolutionStateModel) {
        return state.relevantResultsPerSolutionCode;
    }

    @Selector([SolutionState])
    static getIsStale(state: SolutionStateModel) {
        return state.isStale;
    }

    @Selector([SolutionState])
    static getIsLoading(state: SolutionStateModel) {
        return state.isLoading;
    }
}
